import Vue from 'vue';
import utils from '../util';

// Clarity project IDs for different environments
const CLARITY_PROJECT_IDS = {
  dev: 'rywipifwgt', // DEV project ID
  stg: 'rywdq2m3q9', // STG project ID
  prod: null, // PRD setup is out of scope for now
};

// Flag to prevent multiple initializations
let isInitialized = false;

// Get the current environment
const getCurrentEnv = (): string => {
  const env = utils.getEnvForProductLink();
  return env === 'prod' ? 'prod' : env === 'stg' ? 'stg' : 'dev';
};

// Official Microsoft Clarity tracking script
const injectClarityScript = (projectId: string) => {
  // Check if Clarity is already loaded
  if ((window as any).clarity) {
    console.log('Clarity already loaded');
    return;
  }

  // Create the official Clarity script
  const script = document.createElement('script');
  script.innerHTML = `
    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");
  `;

  // Insert the script into the head
  document.head.appendChild(script);

  console.log(`Clarity initialized for ${getCurrentEnv()} environment with project ID: ${projectId}`);
};

// Initialize Clarity based on environment
export const initClarity = (forceEnv?: string) => {
  // Prevent multiple initializations unless forced
  if (isInitialized && !forceEnv) {
    return;
  }

  const currentEnv = forceEnv || getCurrentEnv();
  const projectId = CLARITY_PROJECT_IDS[currentEnv];

  // Only initialize if we have a project ID for the current environment
  if (projectId) {
    injectClarityScript(projectId);
  } else {
    console.log(`Clarity not initialized for ${currentEnv} environment - no project ID configured`);
  }

  isInitialized = true;
};

// Reset function for testing
export const resetClarity = () => {
  isInitialized = false;
  delete (window as any).clarity;
};

// Only auto-initialize in browser environment and not in Jest
const isJest = typeof process !== 'undefined' && process.env.NODE_ENV === 'test';
if (typeof window !== 'undefined' && typeof document !== 'undefined' && !isJest) {
  initClarity();
}

// Make clarity available on Vue prototype (if loaded)
Vue.prototype.$clarity = (window as any).clarity || {
  // Fallback methods if clarity is not loaded
  track: (event: string, properties?: any) => {
    if ((window as any).clarity) {
      (window as any).clarity('track', event, properties);
    }
  },
  set: (property: string, value: any) => {
    if ((window as any).clarity) {
      (window as any).clarity('set', property, value);
    }
  },
  identify: (userId: string, properties?: any) => {
    if ((window as any).clarity) {
      (window as any).clarity('identify', userId, properties);
    }
  },
};

export default Vue.prototype.$clarity;
